import { AppContext } from "@/App.context";
import config from "@/config";
import {
  API_LOADING_ERROR_CUSTOM_WIDGET,
  API_NO_DATA_FOUND_ERROR_CUSTOM_WIDGET,
  API_NOT_FOUND_ERROR_CUSTOM_WIDGET,
  ERROR_STATUS_CODES,
} from "@/lib/errors/error-constants";
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";

/**
 * @typedef GetAllStatistics
 * @property {GetTotalStatistics} idle_resource_total
 * @property {GetIdleStatistics} idle_resources
 * @property {number} savings_till_date
 * @property {number} savings_available
 */

/**
 * @typedef GetIdleFilters
 * @property {string[]} accounts
 * @property {string[]} rg
 * @property {string[]} regions
 * @property {string[]} tags
 */

// /**
//  * @typedef GetResultStatistics
//  * @property {number} saving_available  - Email address
//  */

/**
 * @typedef GetAllStatisticsRes
 * @property {GetAllStatistics[]} statistics
 *  @property {GetIdleFilters} filters
 */
/**
 * @typedef GetStatisticsReq
 * @property {'aws' | 'gcp' | 'azure' | 'all-cloud' } service
 * @property { 'false' | 'true' } untagged
 */

export const getRiTotalStats = (token) => {
  /**
   * @param {GetStatisticsReq} req
   * @param {AbortSignal=} signal
   * @returns {Promise<GetAllStatistics>}
   */
  return async (
    { service, filters, untagged, period, start_date, end_date },
    signal
  ) => {
    const body = {
      filters: {
        accounts: filters?.accounts || [],
        regions: filters?.regions || [],
        terms: filters?.term || [],
      },
      period: period,
      startDate: start_date,
      endDate: end_date,
    };

    const url = new URL(
      `${config.apiHost}/v1/${service}/ri-utilization/statistics`
    );

    const owners = await fetch(url.toString(), {
      method: "POST",
      body: JSON.stringify(body),
      signal,
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    });
    if (!owners.ok) {
      if (ERROR_STATUS_CODES.includes(owners.status)) {
        throw new Error(API_LOADING_ERROR_CUSTOM_WIDGET);
      }
      if (owners.status === 404) {
        throw new Error(API_NOT_FOUND_ERROR_CUSTOM_WIDGET);
      }
      throw new Error((await owners.json()).message);
    }
    const data = await owners.json();

    if (
      (!data?.total_ri_utilization || data?.total_ri_utilization === 0) &&
      (!data?.unused_ri || data?.unused_ri === 0) &&
      (!data?.expiring_ri || data?.expiring_ri === 0) &&
      (!data?.total_ri_vs_saving || data?.total_ri_vs_saving === 0)
    ) {
      throw new Error(API_NO_DATA_FOUND_ERROR_CUSTOM_WIDGET);
    }

    return data;
  };
};

/**
 * @param {GetStatisticsReq} req
 * @param {any[]=} deps
 */
export const useRiTotalStats = (req, deps = []) => {
  const appContext = useContext(AppContext);
  const query = useQuery({
    queryKey: ["getRiTotalStats", appContext.idToken, req, ...deps],
    queryFn: ({ signal }) => getRiTotalStats(appContext.idToken)(req, signal),
  });
  return query;
};
